import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle, AlertCircle, Clock } from "lucide-react";
import { apiClient } from "../../utils/apiClient/index.ts";
import { DynamicFormField } from "../../components/forms/DynamicFormField";
import Button from "../../components/common/ui/button/Button";
import { MarkdownRenderer } from "../../components/MarkdownRenderer";
import { FormField, ParticipantByTokenDto } from "@secure-caseworker/shared";

/**
 * PublicRequestPage
 *
 * Handles public form submission for request participants.
 * Uses the new participant-based API endpoints.
 *
 * For signature requests, participants will be directed to EmailVerification
 * and then EmbeddedCeremony (Phase 3).
 */
export const PublicRequestPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [participantData, setParticipantData] =
    useState<ParticipantByTokenDto | null>(null);
  const [fieldValues, setFieldValues] = useState<Record<string, any>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const template = participantData?.request.template;
  const request = participantData?.request;

  useEffect(() => {
    loadRequest();
  }, [token]);

  const getErrorMessage = (err: any, context: "load" | "submit"): string => {
    const message = err.message || "";

    if (message.includes("expired")) {
      return context === "load"
        ? "This form has expired and is no longer accepting submissions."
        : "This form has expired while you were filling it out.";
    }

    if (message.includes("404")) {
      return "This form does not exist or the link is invalid.";
    }

    return context === "load"
      ? message || "Failed to load form"
      : message || "Failed to submit form. Please try again.";
  };

  const loadRequest = async () => {
    try {
      const data = await apiClient.getParticipantByToken(token!);
      setParticipantData(data);

      setLoading(false);
    } catch (err: any) {
      setError(getErrorMessage(err, "load"));
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldId: string, value: any) => {
    setFieldValues((prev) => ({ ...prev, [fieldId]: value }));
    // Clear error for this field
    if (fieldErrors[fieldId]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    template?.form_template_fields?.forEach((field: any) => {
      if (field.required && !fieldValues[field.id]) {
        errors[field.id] = "This field is required";
      }
      // For file fields, check if at least one file was uploaded
      if (field.required && field.field_type === "file") {
        const files = fieldValues[field.id];
        if (!files || !Array.isArray(files) || files.length === 0) {
          errors[field.id] = "Please upload at least one file";
        }
      }
    });

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFileUpload = async (
    _fieldId: string,
    file: File,
    onProgress: (percent: number) => void,
  ) => {
    if (!participantData || !participantData.request.output_folder_id) {
      throw new Error("Missing participant or folder information");
    }

    const result = await apiClient.uploadParticipantFile(
      participantData.id,
      file,
      token!,
      participantData.request.output_folder_id,
      onProgress,
    );

    return result.file;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !participantData || !request) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Transform flat fieldValues into FormSubmissionData shape
      const fieldData = {
        fields: (template?.form_template_fields || []).map((field: any) => ({
          key: field.id,
          label: field.label,
          type: field.field_type,
          value: fieldValues[field.id] ?? "",
        })),
        submission_version: 1,
      };
      if (!participantData.request.output_folder_id) {
        throw Error("Something went wrong contact support");
      }
      await apiClient.submitParticipantForm(
        participantData?.id,
        request?.id,
        fieldData,
        token!,
        participantData.request.output_folder_id,
      );
      setSubmitted(true);
    } catch (err: any) {
      setError(getErrorMessage(err, "submit"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading form...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full px-4">
        <div className="text-center max-w-md mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-800 p-8">
            <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Form Unavailable
            </h1>
            <p className="text-gray-600 dark:text-gray-400">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="flex items-center justify-center h-full px-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-800 p-8 max-w-md w-full text-center">
          <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-green-600 mb-4">
            Form Submitted Successfully
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-2">
            Thank you for completing this form.
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Your submission has been received and securely stored.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-800 p-8">
          {/* Header */}
          <div className="mb-6">
            {request?.requestor && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                Requested by{" "}
                {request.requestor.full_name || request.requestor.email}
                {request.created_at && (
                  <> at {new Date(request.created_at).toLocaleString()}</>
                )}
              </p>
            )}
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              {template?.title || "Form Submission"}
            </h1>
            {template?.instructions && (
              <div className="border border-gray-200 mt-4 border-l-4 border-l-brand-500 rounded-lg pl-4 py-2 bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 dark:border-l-brand-500">
                <MarkdownRenderer content={template.instructions} />
              </div>
            )}
          </div>

          {/* Due date notice */}
          {request?.due_date && (
            <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg flex items-start gap-3">
              <Clock className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  Due date: {new Date(request.due_date).toLocaleString()}
                </p>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            {template?.form_template_fields?.map((field: FormField) => (
              <DynamicFormField
                key={field.id}
                field={field}
                value={fieldValues[field.id]}
                onChange={handleFieldChange}
                error={fieldErrors[field.id]}
                onFileUpload={handleFileUpload}
              />
            ))}

            {/* Submit error */}
            {error && !loading && (
              <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-800 dark:text-red-200">
                  {error}
                </p>
              </div>
            )}

            {/* Submit button */}
            <div className="mt-6">
              <Button
                type="submit"
                variant="primary"
                size="md"
                className="w-full"
                disabled={submitting}
              >
                {submitting ? "Submitting..." : "Submit Form"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
